import { GameState } from '../game/GameState';
import type { AdmiralState, FleetState, NavalOrderType, NavalWeather, ShipStackState } from '../types/game';
import type { NavalBattleResult, NavalShipOutcome } from '../types/naval';
import { DataRegistry } from './DataRegistry';
import { ArmyManager } from './ArmyManager';

const FACTION_SHIPS: Record<string, string[]> = {
  majapahit: ['war_boat', 'junk', 'transport'], sunda: ['war_boat', 'transport'], demak: ['war_boat', 'galley', 'transport'], cirebon: ['war_boat', 'junk', 'transport'], banten: ['galley', 'junk', 'transport'], mataram: ['war_boat', 'transport'], blambangan: ['war_boat', 'dhow', 'transport'], bali: ['war_boat', 'dhow', 'transport'], madura: ['dhow', 'war_boat', 'transport'], malacca: ['junk', 'dhow', 'transport'], aceh: ['galley', 'dhow', 'transport'], portuguese: ['galleon', 'galley', 'transport'], dutch_voc: ['fluyt', 'galleon', 'transport'], british_eic: ['galleon', 'fluyt', 'transport'], ming: ['junk', 'war_boat', 'transport'], arab_confederation: ['dhow', 'galley', 'transport'],
};

export class NavalManager {
  static readonly instance = new NavalManager();

  ensureCampaignInitialized() {
    const campaign = GameState.instance.requireCampaign();
    if (Object.keys(campaign.fleets).length || !DataRegistry.instance.shipList().length || !DataRegistry.instance.seaZoneList().length) return;
    for (const faction of Object.values(campaign.factions)) {
      const ports = Object.values(campaign.provinces)
        .filter((province) => province.owner === faction.id && province.controller === faction.id && DataRegistry.instance.provinces[province.id]?.port)
        .sort((a, b) => (b.portLevel ?? 0) - (a.portLevel ?? 0) || b.population - a.population);
      if (!ports.length) continue;
      const port = ports[0];
      const ids = FACTION_SHIPS[faction.id] ?? ['war_boat', 'transport'];
      const level = Math.max(1, port.portLevel ?? 1);
      const composition = level >= 3 ? [ids[0], ids[0], ids[1] ?? ids[0], ids[2] ?? 'transport'] : level === 2 ? [ids[0], ids[1] ?? ids[0], ids[2] ?? 'transport'] : [ids[0], ids[2] ?? 'transport'];
      this.createFleet(faction.id, port.id, `${DataRegistry.instance.factions[faction.id]?.name ?? faction.id} Sea Guard`, composition, true);
    }
    campaign.selectedFleetId = Object.values(campaign.fleets).find((fleet) => fleet.factionId === campaign.playerFactionId)?.id;
  }

  processMonth() {
    this.ensureCampaignInitialized();
    this.processWeather();
    this.processShipbuilding();
    this.advanceFleetMovements();
    this.processMaintenanceRepairAndAttrition();
    this.refreshSeaControl();
    this.processRaiding();
    this.resolveInterceptions();
    this.processAmphibiousOperations();
    this.processAI();
    this.refreshBlockades();
  }

  createFleet(factionId: string, provinceId: string, name?: string, shipIds: string[] = [], free = false) {
    const campaign = GameState.instance.requireCampaign();
    const province = campaign.provinces[provinceId];
    const data = DataRegistry.instance.provinces[provinceId];
    if (!province || !data?.port || province.owner !== factionId || province.controller !== factionId) return undefined;
    const fleetIndex = Object.values(campaign.fleets).filter((fleet) => fleet.factionId === factionId).length + 1;
    const id = `fleet_${factionId}_${fleetIndex}_${campaign.turn}`;
    const admiralId = `admiral_${id}`;
    const ships: ShipStackState[] = [];
    for (const shipId of shipIds) {
      const ship = DataRegistry.instance.ships[shipId];
      if (!ship || (province.portLevel ?? 1) < ship.requiredPortLevel) continue;
      if (!free && campaign.factions[factionId].treasury < ship.costGold) continue;
      if (!free) campaign.factions[factionId].treasury -= ship.costGold;
      ships.push(this.createShipStack(id, shipId, ships.length));
    }
    const fleet: FleetState = { id, factionId, name: name ?? `Fleet ${fleetIndex}`, baseProvinceId: provinceId, seaZoneId: this.zoneForPort(provinceId)?.id, order: 'hold', admiralId, ships, embarkedArmyIds: [], transportUsed: 0 };
    campaign.fleets[id] = fleet;
    campaign.admirals[admiralId] = this.createAdmiral(admiralId, id, factionId);
    return fleet;
  }

  queueShip(factionId: string, provinceId: string, shipId: string) {
    const campaign = GameState.instance.requireCampaign();
    const province = campaign.provinces[provinceId];
    const ship = DataRegistry.instance.ships[shipId];
    if (!province || !ship || province.owner !== factionId || province.controller !== factionId || !DataRegistry.instance.provinces[provinceId]?.port) return false;
    if ((province.portLevel ?? 0) < ship.requiredPortLevel || campaign.factions[factionId].treasury < ship.costGold) return false;
    const timberNeed = Math.max(2, Math.round(ship.hull / 28));
    if ((province.storage.timber ?? 0) < timberNeed) return false;
    campaign.factions[factionId].treasury -= ship.costGold;
    province.storage.timber = Math.max(0, (province.storage.timber ?? 0) - timberNeed);
    campaign.shipbuildingQueue.push({ id: `shipbuild_${factionId}_${campaign.turn}_${campaign.shipbuildingQueue.length}`, factionId, provinceId, shipId, remainingMonths: Math.max(1, ship.buildMonths - Math.max(0, (province.portLevel ?? 1) - 1)) });
    GameState.instance.log(`${ship.name} laid down at ${DataRegistry.instance.provinces[provinceId]?.name}.`, factionId === campaign.playerFactionId ? 'success' : 'info');
    return true;
  }

  issueOrder(fleetId: string, order: NavalOrderType, destinationSeaZoneId?: string) {
    const campaign = GameState.instance.requireCampaign();
    const fleet = campaign.fleets[fleetId];
    if (!fleet) return false;
    if (destinationSeaZoneId && !DataRegistry.instance.seaZones[destinationSeaZoneId]) return false;
    fleet.order = order;
    fleet.destinationSeaZoneId = destinationSeaZoneId;
    if (order === 'return') fleet.destinationSeaZoneId = this.zoneForPort(fleet.baseProvinceId)?.id;
    return true;
  }

  fleetStrength(fleetId: string) {
    const campaign = GameState.instance.requireCampaign();
    const fleet = campaign.fleets[fleetId];
    if (!fleet) return 0;
    const admiral = fleet.admiralId ? campaign.admirals[fleet.admiralId] : undefined;
    const tradition = campaign.factions[fleet.factionId]?.navalTradition ?? 0;
    const weather = fleet.seaZoneId ? campaign.seaZones[fleet.seaZoneId] : undefined;
    const weatherPenalty = weather ? 1 - weather.weatherSeverity * 0.22 : 1;
    return fleet.ships.reduce((sum, stack) => {
      const ship = DataRegistry.instance.ships[stack.shipId];
      if (!ship) return sum;
      const hullRatio = stack.hull / Math.max(1, ship.hull);
      const moraleRatio = stack.morale / Math.max(1, ship.morale);
      return sum + (ship.firepower * 5 + ship.hull * 0.65 + ship.maneuver * 0.25 + ship.speed * 0.18) * hullRatio * moraleRatio * (1 + stack.experience / 150);
    }, 0) * (1 + (admiral?.command ?? 0) * 0.035 + (admiral?.gunnery ?? 0) * 0.025 + tradition * 0.0025) * weatherPenalty;
  }

  transportCapacity(fleetId: string) {
    const fleet = GameState.instance.requireCampaign().fleets[fleetId];
    if (!fleet) return 0;
    return fleet.ships.reduce((sum, stack) => sum + (DataRegistry.instance.ships[stack.shipId]?.transportCapacity ?? 0) * Math.max(0.2, stack.hull / Math.max(1, DataRegistry.instance.ships[stack.shipId]?.hull ?? 1)), 0);
  }

  embarkArmy(fleetId: string, armyId: string) {
    const campaign = GameState.instance.requireCampaign();
    const fleet = campaign.fleets[fleetId];
    const army = campaign.armies[armyId];
    if (!fleet || !army || fleet.factionId !== army.factionId || army.provinceId !== fleet.baseProvinceId) return false;
    const need = this.armyTransportNeed(armyId);
    if (fleet.transportUsed + need > this.transportCapacity(fleetId)) return false;
    if (!fleet.embarkedArmyIds.includes(armyId)) fleet.embarkedArmyIds.push(armyId);
    fleet.transportUsed += need;
    army.embarkedByFleetId = fleetId;
    return true;
  }

  disembarkArmy(fleetId: string, armyId: string) {
    const campaign = GameState.instance.requireCampaign();
    const fleet = campaign.fleets[fleetId];
    const army = campaign.armies[armyId];
    if (!fleet || !army || !fleet.embarkedArmyIds.includes(armyId)) return false;
    fleet.embarkedArmyIds = fleet.embarkedArmyIds.filter((id) => id !== armyId);
    fleet.transportUsed = Math.max(0, fleet.transportUsed - this.armyTransportNeed(armyId));
    delete army.embarkedByFleetId;
    return true;
  }

  beginAmphibiousOperation(fleetId: string, targetProvinceId: string, armyIds?: string[]) {
    const campaign = GameState.instance.requireCampaign();
    const fleet = campaign.fleets[fleetId];
    const target = campaign.provinces[targetProvinceId];
    const zone = fleet?.seaZoneId ? DataRegistry.instance.seaZones[fleet.seaZoneId] : undefined;
    if (!fleet || !target || !zone?.adjacentPorts.includes(targetProvinceId) || !DataRegistry.instance.provinces[targetProvinceId]?.port) return false;
    const selectedArmies = armyIds?.length ? armyIds : fleet.embarkedArmyIds;
    if (!selectedArmies.length || selectedArmies.some((id) => !fleet.embarkedArmyIds.includes(id))) return false;
    if (target.owner !== fleet.factionId && !this.areEnemies(fleet.factionId, target.owner)) return false;
    const id = `amphib_${fleet.id}_${targetProvinceId}_${campaign.turn}`;
    campaign.amphibiousOperations[id] = { id, factionId: fleet.factionId, fleetId, targetProvinceId, armyIds: [...selectedArmies], remainingMonths: target.owner === fleet.factionId ? 1 : Math.max(1, (target.fortLevel ?? 0) + 1), status: 'landing' };
    fleet.order = 'transport';
    GameState.instance.log(`Amphibious operation begun against ${DataRegistry.instance.provinces[targetProvinceId]?.name}.`, fleet.factionId === campaign.playerFactionId ? 'warning' : 'danger');
    return true;
  }

  resolveBattle(attackerFleetId: string, defenderFleetId: string): NavalBattleResult | undefined {
    const campaign = GameState.instance.requireCampaign();
    const attacker = campaign.fleets[attackerFleetId];
    const defender = campaign.fleets[defenderFleetId];
    if (!attacker || !defender || !attacker.seaZoneId || attacker.seaZoneId !== defender.seaZoneId || !this.areEnemies(attacker.factionId, defender.factionId)) return undefined;
    const zoneState = campaign.seaZones[attacker.seaZoneId];
    const seed = this.hash(`${campaign.turn}:${attacker.id}:${defender.id}`);
    const attackerPower = this.fleetStrength(attacker.id) * this.orderMultiplier(attacker.order, defender) * (0.9 + this.random(seed) * 0.2);
    const defenderPower = this.fleetStrength(defender.id) * this.orderMultiplier(defender.order, attacker) * (0.9 + this.random(seed + 7) * 0.2);
    const attackerWins = attackerPower >= defenderPower;
    const ratio = Math.max(0.35, Math.min(2.8, attackerPower / Math.max(1, defenderPower)));
    const attackerDamage = Math.min(0.68, (attackerWins ? 0.12 : 0.28) / Math.max(0.65, ratio));
    const defenderDamage = Math.min(0.72, (attackerWins ? 0.3 : 0.13) * Math.max(0.75, ratio));
    const attackerOutcomes = this.applyFleetDamage(attacker, attackerDamage, seed + 11);
    const defenderOutcomes = this.applyFleetDamage(defender, defenderDamage, seed + 19);
    const winner = attackerWins ? attacker : defender;
    const loser = attackerWins ? defender : attacker;
    const attackerLosses = attackerOutcomes.filter((entry) => entry.sunk).length;
    const defenderLosses = defenderOutcomes.filter((entry) => entry.sunk).length;
    const result: NavalBattleResult = {
      attackerFleetId, defenderFleetId, seaZoneId: attacker.seaZoneId, winnerFleetId: winner.id, loserFleetId: loser.id, attackerFactionId: attacker.factionId, defenderFactionId: defender.factionId, winnerFactionId: winner.factionId, weather: zoneState?.weather ?? 'clear', attackerLosses, defenderLosses, attackerRemaining: attacker.ships.length, defenderRemaining: defender.ships.length, decisive: Math.abs(attackerPower - defenderPower) / Math.max(attackerPower, defenderPower, 1) > 0.35, attackerOutcomes, defenderOutcomes,
    };
    this.registerBattle(result);
    loser.order = 'return';
    loser.destinationSeaZoneId = this.zoneForPort(loser.baseProvinceId)?.id;
    return result;
  }

  refreshBlockades() {
    const campaign = GameState.instance.requireCampaign();
    for (const province of Object.values(campaign.provinces)) delete province.blockadedBy;
    for (const fleet of Object.values(campaign.fleets)) {
      if (fleet.order !== 'blockade' || !fleet.seaZoneId || !fleet.ships.length) continue;
      const zone = DataRegistry.instance.seaZones[fleet.seaZoneId];
      for (const provinceId of zone?.adjacentPorts ?? []) {
        const province = campaign.provinces[provinceId];
        if (province && this.areEnemies(fleet.factionId, province.owner)) province.blockadedBy = fleet.factionId;
      }
    }
    for (const province of Object.values(campaign.provinces)) {
      if (!DataRegistry.instance.provinces[province.id]?.port || province.blockadedBy) continue;
      const hostileArmy = Object.values(campaign.armies).find((army) => !army.embarkedByFleetId && army.provinceId === province.id && this.areEnemies(army.factionId, province.owner));
      if (hostileArmy) province.blockadedBy = hostileArmy.factionId;
    }
    for (const war of Object.values(campaign.wars)) war.blockadedProvinces = Object.values(campaign.provinces).filter((province) => province.blockadedBy && (war.attackers.includes(province.blockadedBy) || war.defenders.includes(province.blockadedBy))).map((province) => province.id);
  }

  zoneForPort(provinceId: string) { return DataRegistry.instance.seaZoneList().find((zone) => zone.adjacentPorts.includes(provinceId)); }
  fleetsInZone(seaZoneId: string) { return Object.values(GameState.instance.requireCampaign().fleets).filter((fleet) => fleet.seaZoneId === seaZoneId && fleet.ships.length); }
  areEnemies(firstFactionId: string, secondFactionId: string) { return GameState.instance.requireCampaign().factions[firstFactionId]?.atWarWith.includes(secondFactionId) ?? false; }

  private processShipbuilding() {
    const campaign = GameState.instance.requireCampaign();
    for (const item of campaign.shipbuildingQueue) item.remainingMonths -= 1;
    for (const item of campaign.shipbuildingQueue.filter((entry) => entry.remainingMonths <= 0)) {
      const fleet = Object.values(campaign.fleets).find((entry) => entry.factionId === item.factionId && entry.baseProvinceId === item.provinceId) ?? this.createFleet(item.factionId, item.provinceId);
      if (fleet) fleet.ships.push(this.createShipStack(fleet.id, item.shipId, fleet.ships.length));
      GameState.instance.log(`${DataRegistry.instance.ships[item.shipId]?.name ?? item.shipId} completed at ${DataRegistry.instance.provinces[item.provinceId]?.name}.`, item.factionId === campaign.playerFactionId ? 'success' : 'info');
    }
    campaign.shipbuildingQueue = campaign.shipbuildingQueue.filter((entry) => entry.remainingMonths > 0);
  }

  private advanceFleetMovements() {
    const campaign = GameState.instance.requireCampaign();
    for (const fleet of Object.values(campaign.fleets)) {
      if (!fleet.destinationSeaZoneId || fleet.destinationSeaZoneId === fleet.seaZoneId || !fleet.seaZoneId) { if (fleet.destinationSeaZoneId === fleet.seaZoneId) delete fleet.destinationSeaZoneId; continue; }
      const path = this.seaPath(fleet.seaZoneId, fleet.destinationSeaZoneId);
      if (path.length < 2) { delete fleet.destinationSeaZoneId; continue; }
      fleet.seaZoneId = path[1];
      if (fleet.seaZoneId === fleet.destinationSeaZoneId) delete fleet.destinationSeaZoneId;
    }
  }

  private processWeather() {
    const campaign = GameState.instance.requireCampaign();
    const monsoonSeason = campaign.month >= 10 || campaign.month <= 2;
    for (const zone of DataRegistry.instance.seaZoneList()) {
      const state = campaign.seaZones[zone.id] ?? (campaign.seaZones[zone.id] = { id: zone.id, weather: 'clear', weatherSeverity: 0, patrolledBy: [] });
      const roll = this.random(this.hash(`${zone.id}:${campaign.turn}`));
      const risk = zone.monsoonRisk * (monsoonSeason ? 1.35 : 0.55);
      if (roll < risk * 0.18) { state.weather = 'monsoon'; state.weatherSeverity = Math.min(1, 0.68 + roll); }
      else if (roll < risk * 0.42) { state.weather = 'storm'; state.weatherSeverity = 0.48 + risk * 0.28; }
      else if (roll < risk * 0.72) { state.weather = 'squall'; state.weatherSeverity = 0.22 + risk * 0.22; }
      else { state.weather = 'clear'; state.weatherSeverity = 0; }
    }
  }

  private processMaintenanceRepairAndAttrition() {
    const campaign = GameState.instance.requireCampaign();
    for (const fleet of Object.values(campaign.fleets)) {
      const faction = campaign.factions[fleet.factionId];
      const admiral = fleet.admiralId ? campaign.admirals[fleet.admiralId] : undefined;
      const upkeep = fleet.ships.reduce((sum, stack) => sum + (DataRegistry.instance.ships[stack.shipId]?.upkeepGold ?? 0), 0);
      if (faction.treasury >= upkeep) faction.treasury -= upkeep;
      else for (const stack of fleet.ships) stack.morale = Math.max(5, stack.morale - 6);
      const zoneState = fleet.seaZoneId ? campaign.seaZones[fleet.seaZoneId] : undefined;
      const navigationReduction = (admiral?.navigation ?? 0) * 0.035;
      const attritionRate = Math.max(0, (zoneState?.weatherSeverity ?? 0) * 0.08 - navigationReduction * 0.02 + (fleet.embarkedArmyIds.length ? 0.006 : 0));
      let attrition = 0;
      for (const stack of fleet.ships) {
        const ship = DataRegistry.instance.ships[stack.shipId];
        if (!ship) continue;
        const loss = Math.min(stack.hull - 1, Math.max(0, Math.round(ship.hull * attritionRate)));
        stack.hull -= loss; attrition += loss; stack.morale = Math.max(5, stack.morale - Math.round(attritionRate * 90));
      }
      fleet.lastAttrition = attrition;
      const atBase = fleet.seaZoneId === this.zoneForPort(fleet.baseProvinceId)?.id;
      if (atBase && ['hold', 'return'].includes(fleet.order)) {
        const port = campaign.provinces[fleet.baseProvinceId];
        for (const stack of fleet.ships) { const maxHull = DataRegistry.instance.ships[stack.shipId]?.hull ?? stack.hull; stack.hull = Math.min(maxHull, stack.hull + 4 + (port?.portLevel ?? 1) * 3); stack.morale = Math.min(DataRegistry.instance.ships[stack.shipId]?.morale ?? 100, stack.morale + 5); }
      }
      fleet.ships = fleet.ships.filter((stack) => stack.hull > 0);
      if (attrition > 0 && fleet.factionId === campaign.playerFactionId) GameState.instance.log(`${fleet.name} suffered ${attrition} hull damage in ${zoneState?.weather ?? 'rough'} seas.`, 'warning');
    }
  }

  private refreshSeaControl() {
    const campaign = GameState.instance.requireCampaign();
    for (const state of Object.values(campaign.seaZones)) { state.patrolledBy = []; delete state.controller; delete state.raidedBy; }
    for (const zone of DataRegistry.instance.seaZoneList()) {
      const fleets = this.fleetsInZone(zone.id);
      const powers = new Map<string, number>();
      for (const fleet of fleets) { powers.set(fleet.factionId, (powers.get(fleet.factionId) ?? 0) + this.fleetStrength(fleet.id)); if (['patrol', 'intercept', 'escort', 'blockade'].includes(fleet.order)) campaign.seaZones[zone.id].patrolledBy.push(fleet.factionId); }
      const sorted = [...powers.entries()].sort((a, b) => b[1] - a[1]);
      if (sorted[0] && (!sorted[1] || sorted[0][1] > sorted[1][1] * 1.2)) campaign.seaZones[zone.id].controller = sorted[0][0];
    }
  }

  private processRaiding() {
    const campaign = GameState.instance.requireCampaign();
    for (const fleet of Object.values(campaign.fleets)) {
      if (fleet.order !== 'raid' || !fleet.seaZoneId) continue;
      const zone = DataRegistry.instance.seaZones[fleet.seaZoneId];
      const victims = [...new Set((zone?.adjacentPorts ?? []).map((id) => campaign.provinces[id]?.owner).filter((id): id is string => Boolean(id && this.areEnemies(fleet.factionId, id))))];
      if (!victims.length) continue;
      campaign.seaZones[fleet.seaZoneId].raidedBy = fleet.factionId;
      const value = Math.max(2, Math.round((zone?.tradeValue ?? 5) * Math.min(1.5, this.fleetStrength(fleet.id) / 400)));
      for (const victimId of victims) campaign.factions[victimId].treasury = Math.max(0, campaign.factions[victimId].treasury - value);
      campaign.factions[fleet.factionId].treasury += Math.round(value * 0.65);
      for (const provinceId of zone?.adjacentPorts ?? []) if (campaign.provinces[provinceId] && victims.includes(campaign.provinces[provinceId].owner)) campaign.provinces[provinceId].prosperity = Math.max(0, campaign.provinces[provinceId].prosperity - 1);
    }
  }

  private resolveInterceptions() {
    const campaign = GameState.instance.requireCampaign();
    for (const zone of DataRegistry.instance.seaZoneList()) {
      const fleets = this.fleetsInZone(zone.id).filter((fleet) => fleet.lastBattleTurn !== campaign.turn);
      for (let left = 0; left < fleets.length; left++) for (let right = left + 1; right < fleets.length; right++) {
        const first = fleets[left]; const second = fleets[right];
        if (!this.areEnemies(first.factionId, second.factionId)) continue;
        const aggressive = ['intercept', 'patrol', 'blockade', 'raid'].includes(first.order) || ['intercept', 'patrol', 'blockade', 'raid'].includes(second.order);
        if (!aggressive) continue;
        first.lastBattleTurn = campaign.turn; second.lastBattleTurn = campaign.turn; this.resolveBattle(first.id, second.id); break;
      }
    }
  }

  private processAmphibiousOperations() {
    const campaign = GameState.instance.requireCampaign();
    for (const operation of Object.values(campaign.amphibiousOperations)) {
      const fleet = campaign.fleets[operation.fleetId];
      if (!fleet || !fleet.ships.length) { operation.status = 'cancelled'; continue; }
      operation.remainingMonths -= 1;
      if (operation.remainingMonths > 0) continue;
      const target = campaign.provinces[operation.targetProvinceId];
      if (!target) { operation.status = 'cancelled'; continue; }
      for (const armyId of operation.armyIds) {
        const army = campaign.armies[armyId]; if (!army) continue;
        this.disembarkArmy(fleet.id, armyId); army.provinceId = target.id; army.stance = 'attack';
        if (target.owner !== army.factionId) for (const stack of army.units) { stack.strength = Math.max(1, Math.round(stack.strength * (target.blockadedBy === army.factionId ? 0.96 : 0.88))); stack.morale = Math.max(5, stack.morale - (target.blockadedBy === army.factionId ? 4 : 12)); }
      }
      const leadArmy = operation.armyIds.map((id) => campaign.armies[id]).find(Boolean);
      if (leadArmy && target.owner !== operation.factionId) ArmyManager.instance.resolveCampaignSkirmish(leadArmy.id);
      operation.status = 'completed';
      GameState.instance.log(`${DataRegistry.instance.factions[operation.factionId]?.name ?? operation.factionId} landed at ${DataRegistry.instance.provinces[target.id]?.name}.`, operation.factionId === campaign.playerFactionId ? 'success' : 'danger');
    }
    for (const [id, operation] of Object.entries(campaign.amphibiousOperations)) if (['completed', 'cancelled'].includes(operation.status)) delete campaign.amphibiousOperations[id];
  }

  private processAI() {
    const campaign = GameState.instance.requireCampaign();
    for (const faction of Object.values(campaign.factions)) {
      if (faction.id === campaign.playerFactionId) continue;
      const fleets = Object.values(campaign.fleets).filter((fleet) => fleet.factionId === faction.id && fleet.ships.length);
      const ports = Object.values(campaign.provinces).filter((province) => province.owner === faction.id && province.controller === faction.id && DataRegistry.instance.provinces[province.id]?.port);
      if (!fleets.length && ports.length) this.createFleet(faction.id, ports[0].id);
      if (campaign.turn % 6 === 0 && ports.length && faction.treasury > 110 && campaign.shipbuildingQueue.filter((item) => item.factionId === faction.id).length < 2) {
        const preferred = FACTION_SHIPS[faction.id] ?? ['war_boat', 'transport'];
        const shipId = preferred[Math.min(preferred.length - 1, (campaign.turn / 6) % preferred.length)];
        this.queueShip(faction.id, ports.sort((a, b) => (b.portLevel ?? 0) - (a.portLevel ?? 0))[0].id, shipId);
      }
      for (const fleet of fleets) {
        const enemyPorts = Object.values(campaign.provinces).filter((province) => this.areEnemies(faction.id, province.owner) && DataRegistry.instance.provinces[province.id]?.port);
        if (enemyPorts.length) {
          const target = enemyPorts.sort((a, b) => (b.portLevel ?? 0) - (a.portLevel ?? 0) || b.prosperity - a.prosperity)[0];
          const targetZone = this.zoneForPort(target.id);
          fleet.order = this.transportCapacity(fleet.id) > 80 && fleet.embarkedArmyIds.length ? 'transport' : 'blockade';
          fleet.destinationSeaZoneId = targetZone?.id;
        } else {
          const personality = DataRegistry.instance.factions[faction.id]?.aiPersonality ?? '';
          fleet.order = personality.includes('merchant') ? 'escort' : personality.includes('raider') ? 'raid' : 'patrol';
          if (!fleet.destinationSeaZoneId) fleet.destinationSeaZoneId = DataRegistry.instance.seaZoneList().sort((a, b) => b.tradeValue - a.tradeValue)[0]?.id;
        }
      }
    }
  }

  private registerBattle(result: NavalBattleResult) {
    const campaign = GameState.instance.requireCampaign();
    const winnerFleet = campaign.fleets[result.winnerFleetId]; const loserFleet = campaign.fleets[result.loserFleetId];
    const winnerAdmiral = winnerFleet?.admiralId ? campaign.admirals[winnerFleet.admiralId] : undefined; const loserAdmiral = loserFleet?.admiralId ? campaign.admirals[loserFleet.admiralId] : undefined;
    if (winnerAdmiral) winnerAdmiral.victories += 1; if (loserAdmiral) loserAdmiral.defeats += 1;
    campaign.factions[result.winnerFactionId].navalTradition = Math.min(100, campaign.factions[result.winnerFactionId].navalTradition + (result.decisive ? 4 : 2));
    campaign.factions[result.loserFleetId ? result.loserFleetId : result.defenderFactionId]?.navalTradition;
    campaign.navalBattleHistory.unshift({ id: `naval_${campaign.turn}_${result.attackerFleetId}_${result.defenderFleetId}`, turn: campaign.turn, seaZoneId: result.seaZoneId, attackerFactionId: result.attackerFactionId, defenderFactionId: result.defenderFactionId, winnerFactionId: result.winnerFactionId, attackerLosses: result.attackerLosses, defenderLosses: result.defenderLosses, weather: result.weather, decisive: result.decisive });
    campaign.navalBattleHistory = campaign.navalBattleHistory.slice(0, 50);
    const war = Object.values(campaign.wars).find((entry) => (entry.attackers.includes(result.attackerFactionId) && entry.defenders.includes(result.defenderFactionId)) || (entry.defenders.includes(result.attackerFactionId) && entry.attackers.includes(result.defenderFactionId)));
    if (war) {
      const value = Math.max(3, Math.min(18, 4 + (result.attackerLosses + result.defenderLosses) * 3 + (result.decisive ? 4 : 0)));
      if (war.attackers.includes(result.winnerFactionId)) war.attackerBattleScore += value; else war.defenderBattleScore += value;
      war.lastMajorEvent = `${DataRegistry.instance.factions[result.winnerFactionId]?.name ?? result.winnerFactionId} won a naval battle in ${DataRegistry.instance.seaZones[result.seaZoneId]?.name ?? result.seaZoneId}.`;
    }
    GameState.instance.log(`${DataRegistry.instance.factions[result.winnerFactionId]?.name ?? result.winnerFactionId} won the naval battle of ${DataRegistry.instance.seaZones[result.seaZoneId]?.name ?? result.seaZoneId}.`, result.winnerFactionId === campaign.playerFactionId ? 'success' : 'danger');
  }

  private applyFleetDamage(fleet: FleetState, damageRatio: number, seed: number): NavalShipOutcome[] {
    const outcomes: NavalShipOutcome[] = [];
    for (let index = 0; index < fleet.ships.length; index++) {
      const stack = fleet.ships[index]; const ship = DataRegistry.instance.ships[stack.shipId]; if (!ship) continue;
      const variation = 0.7 + this.random(seed + index * 13) * 0.7; const hullLost = Math.max(1, Math.round(ship.hull * damageRatio * variation)); const moraleLost = Math.max(2, Math.round(damageRatio * 55 * variation));
      stack.hull = Math.max(0, stack.hull - hullLost); stack.morale = Math.max(0, stack.morale - moraleLost); if (stack.hull > 0) stack.experience = Math.min(100, stack.experience + 2);
      outcomes.push({ stackId: stack.id, hullLost, moraleLost, sunk: stack.hull <= 0 });
    }
    fleet.ships = fleet.ships.filter((stack) => stack.hull > 0);
    return outcomes;
  }

  private orderMultiplier(order: NavalOrderType, enemy: FleetState) {
    if (order === 'intercept') return 1.08;
    if (order === 'patrol') return 1.04;
    if (order === 'blockade') return 0.96;
    if (order === 'raid') return enemy.order === 'escort' ? 0.9 : 1.03;
    if (order === 'escort') return enemy.order === 'raid' ? 1.12 : 1;
    if (order === 'transport') return 0.72;
    if (order === 'return') return 0.82;
    return 1;
  }

  private createShipStack(fleetId: string, shipId: string, index: number): ShipStackState { const ship = DataRegistry.instance.ships[shipId]; return { id: `${fleetId}_${shipId}_${index}`, shipId, hull: ship?.hull ?? 60, morale: ship?.morale ?? 50, experience: 0 }; }
  private createAdmiral(id: string, fleetId: string, factionId: string): AdmiralState { const seed = this.hash(`${fleetId}:${factionId}`); const titles = ['Laksamana', 'Kapitan', 'Admiral', 'Nakhoda', 'Panglima']; const faction = DataRegistry.instance.factions[factionId]?.name.split(' ')[0] ?? factionId; return { id, fleetId, factionId, name: `${titles[seed % titles.length]} ${faction}`, command: 2 + (seed % 5), navigation: 2 + ((seed >> 2) % 5), gunnery: 1 + ((seed >> 4) % 5), loyalty: 55 + (seed % 36), traits: [seed % 2 ? 'sea_wolf' : 'careful_navigator', seed % 3 ? 'formation_sailor' : 'storm_hardened'], victories: 0, defeats: 0 }; }
  private armyTransportNeed(armyId: string) { const army = GameState.instance.requireCampaign().armies[armyId]; return army ? Math.max(20, Math.ceil(army.units.reduce((sum, stack) => sum + stack.strength, 0) / 5)) : 0; }

  private seaPath(start: string, goal: string) {
    if (start === goal) return [start];
    const queue = [start]; const previous = new Map<string, string | undefined>([[start, undefined]]);
    while (queue.length) { const current = queue.shift()!; for (const next of DataRegistry.instance.seaZones[current]?.neighbors ?? []) { if (previous.has(next)) continue; previous.set(next, current); if (next === goal) { const path = [goal]; let cursor: string | undefined = current; while (cursor) { path.unshift(cursor); cursor = previous.get(cursor); } return path; } queue.push(next); } }
    return [];
  }

  private hash(value: string) { let hash = 2166136261; for (const character of value) { hash ^= character.charCodeAt(0); hash = Math.imul(hash, 16777619); } return hash >>> 0; }
  private random(seed: number) { const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453; return x - Math.floor(x); }
}